import React, { type CSSProperties } from 'react';
import { Backdrop, Stack, Typography, styled } from '@mui/material';
import { ReactComponent as SpinnerIcon } from 'assets/images/spinner-gap.svg';
import './animation.css';
import { useLocation } from 'react-router-dom';
import translation from 'i18n/translation.json';
import { ReactComponent as PhotoLoader } from 'assets/images/photo-loader.svg';

interface LoaderProps {
  open: boolean;
  fullScreen?: boolean;
  photoUploadLoader?: boolean;
  isInDialog?: boolean;
  styles?: CSSProperties;
  label?: string;
  subtitle?: string;
  children?: React.ReactNode;
}

interface StyledBackdropProps {
  fullScreen: boolean;
  isInDialog: boolean;
  isInAccount: boolean;
}

const StyledBackdrop = styled(Backdrop, {
  shouldForwardProp: prop => prop !== 'fullScreen' && prop !== 'isInDialog' && prop !== 'isInAccount',
})<StyledBackdropProps>(({ theme, fullScreen, isInDialog, isInAccount }) => ({
  position: fullScreen ? 'fixed' : 'relative',
  backgroundColor: fullScreen
    ? '#d0d0d094'
    : isInDialog
    ? 'transparent'
    : isInAccount
    ? '#F3F4FA'
    : theme.palette.common.white,
  ...(!fullScreen &&
    !isInDialog &&
    isInAccount && {
      [theme.breakpoints.down('sm')]: {
        backgroundColor: 'transparent',
      },
    }),
  zIndex: theme.zIndex.drawer + 1,
  height: fullScreen ? '100vh' : '100%',
  padding: isInDialog ? '32px' : 0,
  width: isInDialog ? '100%' : 'auto',
}));

export const Loader = ({
  open,
  fullScreen = false,
  photoUploadLoader = false,
  isInDialog = false,
  styles,
  label,
  subtitle,
  children,
}: LoaderProps) => {
  const { pathname } = useLocation();
  const isInAccount = pathname.includes('account');

  return photoUploadLoader ? (
    <StyledBackdrop open={open} fullScreen={fullScreen} isInDialog={isInDialog} isInAccount={isInAccount} sx={styles}>
      <Stack flexDirection="column" gap={2}>
        <PhotoLoader className="photo-loader" />
        <Typography variant="h2">{label ?? translation.registration.thankyouForTakingPhoto}</Typography>
        <Typography variant="subtitle1">{subtitle ?? translation.registration.waitPhotoIsBeingChecked}</Typography>
      </Stack>
      {children}
    </StyledBackdrop>
  ) : (
    <StyledBackdrop open={open} fullScreen={fullScreen} isInDialog={isInDialog} isInAccount={isInAccount} sx={styles}>
      <SpinnerIcon className="spinner-gap" />
    </StyledBackdrop>
  );
};
